import { defineComponent as _defineComponent } from 'vue'
import { ref, watch, computed } from 'vue';
import BasePopup from "@/components-UI/BasePopup.vue";
import BaseInput from "@/components-UI/BaseInput.vue";
import SvgRangeCalendar from "@/assets/icons/range-calendar.svg";
import BaseTimePicker from "@/components-UI/BaseTimePicker.vue";
import DatePicker from "v-calendar/lib/components/date-picker.umd";
import SvgTrash from "@/assets/icons/trash-dark.svg";
import BaseButtonField from "@/components-UI/BaseButtonField.vue";
import { vMaska } from "maska/vue";
import BaseButtonV2 from "@/components-UI/buttons/BaseButtonV2.vue";

type DateTime = string | Date | { date: string, time: string };
type AvailableDates = { start: Date | null, end: Date | null, days?: number[] };
type RangeDateModel = { start: string; end: string };


export default /*#__PURE__*/_defineComponent({
  __name: 'BaseDateTimePicker',
  props: {
    value: null,
    format: { default: 'YYYY-MM-DD HH:mm' },
    clearable: { type: Boolean, default: true },
    disabled: { type: Boolean },
    readonly: { type: Boolean },
    label: null,
    placeholder: null,
    view: null,
    position: { default: 'bottom' },
    size: { default: 'large' },
    availableDates: null,
    hidePrependIcon: { type: Boolean },
    teleportPopupToBody: { type: Boolean, default: true },
    returnObject: { type: Boolean },
    onlyHours: { type: Boolean },
    setZeroHours: { type: Boolean },
    additionalActionText: null,
    range: { type: Boolean, default: false }
  },
  emits: ['update:value', 'additional-action'],
  setup(__props: any, { emit: emits }) {

const props = __props as { value?: DateTime | RangeDateModel | [string, string] | string[] | null, format: string, clearable: boolean, disabled?: boolean, readonly?: boolean, label?: string, placeholder?: string, view?: 'border-bottom' | 'outlined' | 'empty', position: 'top' | 'right' | 'bottom' | 'left', size: 'small' | 'medium' | 'large', availableDates?: AvailableDates, hidePrependIcon?: boolean, teleportPopupToBody: boolean, returnObject?: boolean, onlyHours?: boolean, setZeroHours?: boolean, additionalActionText?: string, range: boolean }




const isShowPopup = ref<boolean>(false);
const calendarRef = ref<HTMLElement | null>(null);
const calendarLocale = ref({
    id: 'ru',
    firstDayOfWeek: 2,
    masks: {
        weekdays: 'WW',
    },
});
const calendarModelConfig = ref({
    type: 'string',
    mask: 'YYYY-MM-DD',
});
const model = ref<{ date: string, time: string }>({
    date: '',
    time: '',
});
/** Значение для v-calendar в режиме is-range (`null` пока период не выбран полностью) */
const rangeValue = ref<RangeDateModel | null>(null);
const mask = ref<{ date: string, time: string }>({
    date: '##.##.####',
    time: '##:##',
});

const toYyyyMmDd = (v: unknown): string | null => {
    if (v == null || v === '') return null;
    if (v instanceof Date && !isNaN(v.getTime())) {
        return formatDateTime(v, { dateFormat: 'YYYY-MM-DD' }).date;
    }
    const f = formatDateTime(v as DateTime, { dateFormat: 'YYYY-MM-DD' });
    return f.date || null;
};

const parseRangeInput = (input: unknown): RangeDateModel | null => {
    if (input == null || input === '') return null;
    if (Array.isArray(input)) {
        if (input.length < 2) return null;
        const start = toYyyyMmDd(input[0]);
        const end = toYyyyMmDd(input[1]);
        if (start && end) return { start, end };
        return null;
    }
    if (typeof input === 'object' && input !== null && 'start' in input && 'end' in input) {
        const o = input as { start: unknown; end: unknown };
        const start = toYyyyMmDd(o.start);
        const end = toYyyyMmDd(o.end);
        if (start && end) return { start, end };
        return null;
    }
    return null;
};

const getFormat = computed<{ date: string | null, time: string | null }>(() => {
    const format = props.format.split(' ');
    if (format.length === 2) {
        return {
            date: format[0],
            time: format[1],
        };
    } else if (format.includes(':')) {
        return {
            date: null,
            time: format[0],
        };
    } else {
        return {
            date: format[0],
            time: null,
        };
    }
});
const getClasses = computed<string[]>(() => {
    return [
        (!getFormat.value.time || props.range) && 'without-time',
    ];
});
const getTime = computed<string>(() => {
    const value = new Date(`${model.value.date} ${model.value.time}`);
    const hours = String(value.getHours()).padStart(2, '0');
    const minutes = String(value.getMinutes()).padStart(2, '0');
    return {
        hours,
        minutes: props.onlyHours ? '00' : minutes,
    };
});
const getDate = computed<string>(() => {
    const value = new Date(model.value.date);
    const day = String(value.getDate()).padStart(2, '0');
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const year = String(value.getFullYear());
    return `${day}.${month}.${year}`;
});
const getButtonFieldValue = computed<boolean>(() => {
    if (props.range) {
        return !!parseRangeInput(props.value);
    }
    if (Array.isArray(props.value)) {
        return props.value.length > 0;
    }
    if (typeof props.value === 'object' && props.value !== null) {
        const v = props.value as Record<string, unknown>;
        return !!(v.date || v.time);
    }
    return !!props.value;
});
const getButtonViewValue = computed<{ date: string, time: string }>(() => {
    if (props.range) {
        const r = parseRangeInput(props.value);
        if (!r) return { date: '', time: '' };
        const s = formatDateTime(r.start, { dateFormat: 'DD.MM.YYYY' });
        const e = formatDateTime(r.end, { dateFormat: 'DD.MM.YYYY' });
        return { date: `${s.date}—${e.date}`, time: '' };
    }
    return formatDateTime(props.value as DateTime, { dateFormat: 'DD.MM.YYYY' });
});

const isShowFieldSummary = computed(() => {
    if (props.range) {
        return !!getButtonViewValue.value.date;
    }
    return !!(getButtonViewValue.value.date && getButtonViewValue.value.time);
});

const handleClear = (): void => {
    isShowPopup.value = false;

    if (props.range) {
        rangeValue.value = null;
        if (props.returnObject) {
            emits('update:value', {});
        } else {
            emits('update:value', '');
        }
        return;
    }

    if (props.returnObject) {
        emits('update:value', {});
    } else {
        emits('update:value', '');
    }
};

const handleChangeTime = (event: string): void => {
    model.value.time = event;
};

const handleDayClick = (event: any): void => {
    if (validateAvailableDates(event.id)) {
        const dateTime = formatDateTime(event.id);

        if (props.onlyHours || props.setZeroHours) {
            dateTime.time = '00:00';
        }

        model.value = dateTime;
    }
};

const handleChangeDate = (event: string): void => {
    const dateTime = {
        date: parseDDMMYYYYToYYYYMMDD(event),
        time: formatDateTime(new Date()).time,
    };

    if (props.onlyHours || props.setZeroHours) {
        dateTime.time = '00:00';
    }

    initializeDateTime(dateTime);
    calendarRef.value?.move?.(model.value.date);
};

const handleClose = (): void => {
    isShowPopup.value = false;
};

const handleClickOutside = (event: boolean): void => {
    isShowPopup.value = event;
};

const handleApply = (): void => {
    if (props.range) {
        if (!rangeValue.value?.start || !rangeValue.value?.end) return;
        updateRangeValue();
    } else {
        updateValue();
    }
    isShowPopup.value = false;
};

const additionalAction = (): void => {
    if (props.range) {
        if (!rangeValue.value?.start || !rangeValue.value?.end) return;
        updateRangeValue('additional-action');
    } else {
        updateValue('additional-action');
    }
    isShowPopup.value = false;
};


const handleReset = (): void => {
    if (props.range) {
        if (props.returnObject) {
            emits('update:value', {});
        } else {
            emits('update:value', '');
        }
        rangeValue.value = null;
        const cal = calendarRef.value as { move?: (d: string | Date) => void } | null;
        cal?.move?.(new Date());
        return;
    }

    if (props.returnObject) {
        emits('update:value', {});
    } else {
        emits('update:value', '');
    }

    initializeDateTime(undefined);
    calendarRef.value?.move?.(model.value.date);
};

const onRangeInput = (val: { start?: string | Date; end?: string | Date } | null): void => {
    if (!val) {
        rangeValue.value = null;
        return;
    }
    const start = toYyyyMmDd(val.start);
    const end = toYyyyMmDd(val.end);
    if (start && end) {
        rangeValue.value = { start, end };
    }
};

const updateRangeValue = (emitName: 'update:value' | 'additional-action' = 'update:value'): void => {
    if (!rangeValue.value?.start || !rangeValue.value?.end) return;

    const dateFmt = getFormat.value.date || 'YYYY-MM-DD';
    const s = formatDateTime(rangeValue.value.start, { dateFormat: dateFmt });
    const e = formatDateTime(rangeValue.value.end, { dateFormat: dateFmt });

    if (props.returnObject) {
        if (getFormat.value.time) {
            emits(emitName, {
                start: { date: s.date, time: s.time },
                end: { date: e.date, time: e.time },
            });
        } else {
            emits(emitName, { start: { date: s.date }, end: { date: e.date } });
        }
    } else if (getFormat.value.time) {
        emits(emitName, [`${s.date} ${s.time}`, `${e.date} ${e.time}`]);
    } else {
        emits(emitName, [s.date, e.date]);
    }
};

const initializeDateTime = (
    value: DateTime | undefined | null | { date?: string | null, time?: string | null },
): void => {
    if (value == null || value === '' ||
        (typeof value === 'object' && !(value instanceof Date) && (!value.date && !value.time))
    ) {
        const dateTime = formatDateTime(new Date());

        if (validateAvailableDates(dateTime.date)) {
            model.value = dateTime;
            return;
        }

        if (props.availableDates.days && props.availableDates.days.length) {
            const start = props.availableDates.start;
            const end = props.availableDates.end;
            const year = start || end ? (start || end).getFullYear() : new Date().getFullYear();
            let month = start || end ? (start || end).getMonth() : new Date().getMonth();
            const day = props.availableDates.days.at(-1);

            if (start && !validateAvailableDates(formatDateTime(start).date)) {
                month = start.getMonth() + 1;
            }

            model.value = formatDateTime(new Date(year, month, day));
            return;
        }

        if (props.availableDates.start || props.availableDates.end) {
            model.value = formatDateTime(props.availableDates.start || props.availableDates.end);
            return;
        }
    } else {
        const dateTime = formatDateTime(value);

        if (validateAvailableDates(dateTime.date)) {
            model.value = dateTime;
            return;
        }

        if (props.availableDates.days && props.availableDates.days.length) {
            const date = new Date(dateTime.date);
            const start = props.availableDates.start;
            const end = props.availableDates.end;
            const year = start || end ? (start || end).getFullYear() : date.getFullYear();
            let month = start || end ? (start || end).getMonth() : date.getMonth();
            const day = props.availableDates.days.at(-1);

            if (start && !validateAvailableDates(formatDateTime(start).date)) {
                month = date.getMonth() + 1;
            }

            model.value = formatDateTime(new Date(year, month, day));
            return;
        }

        if (props.availableDates.start || props.availableDates.end) {
            model.value = formatDateTime(props.availableDates.start || props.availableDates.end);
            return;
        }
    }
};

const updateValue = (emitName: 'update:value' | 'additional-action' = 'update:value'): void => {
    const value = formatDateTime(model.value, {
        dateFormat: getFormat.value.date,
    });

    if (props.returnObject) {
        if (getFormat.value.time) {
            emits(emitName, value);
        } else {
            emits(emitName, { date: value.date });
        }
    } else {
        emits(emitName, !getFormat.value.time ? value.date : `${value.date} ${value.time}`);
    }
};

const validateAvailableDates = (value: string): boolean => {
    const newDate = new Date(value);

    if (props.availableDates) {
        const { days } = props.availableDates;
        let { start, end } = props.availableDates;

        if (days && days?.length) {
            return !!days.find((el: number) => el === newDate.getDate());
        }

        if (start) {
            start = new Date(start).setHours(0, 0, 0, 0);
        }

        if (end) {
            end = new Date(end).setHours(0, 0, 0, 0);
        }

        if (start && end) {
            return newDate >= start && newDate <= end;
        } else if (start) {
            return newDate >= start;
        } else if (end) {
            return newDate <= end;
        } else {
            return true;
        }
    }

    return true;
};

const parseDDMMYYYYToYYYYMMDD = (dateString: string): string | null => {
    const parts = dateString.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);

    if (!parts) {
        return null;
    }

    const day = parts[1];
    const month = parts[2];
    const year = parts[3];

    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

    if (
        isNaN(date.getTime()) ||
        date.getFullYear() !== parseInt(year) ||
        date.getMonth() !== parseInt(month) - 1 ||
        date.getDate() !== parseInt(day)
    ) {
        return null;
    }

    return `${year}-${month}-${day}`;
};

const formatDateTime = (
    input?: string | Date | { date?: string | Date | null; time?: string | Date | null } | null,
    options: { dateFormat?: string, timeFormat?: string } = {},
): { date: string, time: string } => {
    const defaultOptions = {
        dateFormat: 'YYYY-MM-DD',
        timeFormat: 'HH:mm',
        ...options,
    };

    const result: { date: string, time: string } = { date: '', time: '' };
    const now = new Date();

    if (!input) return result;

    let datePart: Date | null = null;
    let timePart: Date | null = null;

    const parseDateString = (dateStr: string): Date | null => {
        const parsed = new Date(dateStr);
        if (!isNaN(parsed.getTime())) return parsed;

        const dotFormatMatch = dateStr.match(/^(\d{4})\.(\d{2})\.(\d{2})$/);
        if (dotFormatMatch) {
            return new Date(`${dotFormatMatch[1]}-${dotFormatMatch[2]}-${dotFormatMatch[3]}`);
        }

        const dateMatch = dateStr.match(/(\d{4})[-/](\d{2})[-/](\d{2})/) ||
            dateStr.match(/(\d{2})[-/](\d{2})[-/](\d{4})/);

        if (dateMatch) {
            return dateMatch[3]?.length === 4 ?
                new Date(`${dateMatch[3]}-${dateMatch[2]}-${dateMatch[1]}`) :
                new Date(`${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}`);
        }

        return null;
    };

    if (input instanceof Date) {
        datePart = new Date(input);
        timePart = new Date(now);
        timePart.setFullYear(
            input.getFullYear(),
            input.getMonth(),
            input.getDate(),
        );
    } else if (typeof input === 'string') {
        const timeMatch = input.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/) ||
            input.match(/(\d{1,2})[:h](\d{2})\s?(am|pm)?/i);

        if (timeMatch) {
            timePart = new Date(now);
            let hours = parseInt(timeMatch[1]);
            const minutes = parseInt(timeMatch[2]);
            const seconds = timeMatch[3] ? parseInt(timeMatch[3]) : 0;

            if (timeMatch[4]) {
                const period = timeMatch[4].toLowerCase();
                if (period === 'pm' && hours < 12) hours += 12;
                if (period === 'am' && hours === 12) hours = 0;
            }

            timePart.setHours(hours, minutes, seconds);
        }

        const dateStr = timeMatch ? input.replace(timeMatch[0], '').trim() : input;
        if (dateStr) {
            datePart = parseDateString(dateStr);
        }
    } else if (typeof input === 'object' && ('date' in input || 'time' in input)) {
        if (input.date) {
            datePart = input.date instanceof Date ? new Date(input.date) :
                parseDateString(String(input.date));
            if (isNaN(datePart?.getTime() || 0)) datePart = null;
        }

        if (input.time) {
            timePart = input.time instanceof Date ? new Date(input.time) :
                new Date(`1970-01-01T${input.time}`);
            if (isNaN(timePart.getTime())) timePart = null;
        }
    }

    if (!datePart && timePart) {
        datePart = new Date(now);
        datePart.setHours(timePart.getHours(), timePart.getMinutes(), timePart.getSeconds());
    }

    if (datePart && !timePart) {
        timePart = new Date(now);
        timePart.setFullYear(
            datePart.getFullYear(),
            datePart.getMonth(),
            datePart.getDate(),
        );
    }

    if (datePart) {
        const year = datePart.getFullYear();
        const month = String(datePart.getMonth() + 1).padStart(2, '0');
        const day = String(datePart.getDate()).padStart(2, '0');

        result.date = defaultOptions.dateFormat
            .replace('YYYY', String(year))
            .replace('MM', month)
            .replace('DD', day);
    }

    if (timePart) {
        const hours = timePart.getHours();
        const minutes = String(timePart.getMinutes()).padStart(2, '0');
        const seconds = String(timePart.getSeconds()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const twelveHours = hours % 12 || 12;

        result.time = defaultOptions.timeFormat
            .replace('HH', String(hours).padStart(2, '0'))
            .replace('hh', String(twelveHours).padStart(2, '0'))
            .replace('mm', minutes)
            .replace('ss', seconds)
            .replace('A', ampm)
            .replace('a', ampm.toLowerCase());
    }

    return result;
};

watch(
    () => props.value,
    () => {
        if (props.range) {
            rangeValue.value = parseRangeInput(props.value);
        } else {
            initializeDateTime(props.value as DateTime);
        }
    },
    { deep: true, immediate: true },
);

return { __sfc: true,props, emits, isShowPopup, calendarRef, calendarLocale, calendarModelConfig, model, rangeValue, mask, toYyyyMmDd, parseRangeInput, getFormat, getClasses, getTime, getDate, getButtonFieldValue, getButtonViewValue, isShowFieldSummary, handleClear, handleChangeTime, handleDayClick, handleChangeDate, handleClose, handleClickOutside, handleApply, additionalAction, handleReset, onRangeInput, updateRangeValue, initializeDateTime, updateValue, validateAvailableDates, parseDDMMYYYYToYYYYMMDD, formatDateTime, BasePopup, BaseInput, SvgRangeCalendar, BaseTimePicker, DatePicker, SvgTrash, BaseButtonField, vMaska, BaseButtonV2 }
}

})